import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Mail, 
  Phone, 
  Users, 
  Hotel, 
  CreditCard,
  CalendarClock,
  Bus,
  FileText,
  MoreHorizontal,
  Pencil,
  Receipt,
  Send,
  XCircle,
  Trash2,
  RotateCcw,
  AlertTriangle,
  Check,
  Clock,
  ChevronRight,
  PoundSterling,
  Loader2,
  Edit
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { cn } from '../../../lib/utils';
import { API_URL } from '../../../utils/api';
import { toast } from 'sonner';
import EditWeekenderBookingDialog from './EditWeekenderBookingDialog';
import RecordWeekenderPaymentDialog from './RecordWeekenderPaymentDialog';
import WeekenderInvoiceDialog from './WeekenderInvoiceDialog';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  deposit_paid: 'bg-blue-100 text-blue-700 border-blue-200',
  fully_paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  confirmed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
  refunded: 'bg-slate-100 text-slate-600 border-slate-200'
};

const statusLabels = {
  pending: 'Pending Payment',
  deposit_paid: 'Deposit Paid',
  fully_paid: 'Fully Paid',
  confirmed: 'Confirmed',
  cancelled: 'Cancelled',
  refunded: 'Refunded'
};

const formatCurrency = (amount) => {
  return `£${(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Section wrapper component
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        className="w-full flex items-center justify-between py-3 text-left hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Icon className="w-4 h-4 text-slate-400" />
          {title}
        </span>
        <ChevronRight className={cn(
          "w-4 h-4 text-slate-400 transition-transform",
          open && "rotate-90"
        )} />
      </button>
      {open && (
        <div className="pb-4 pl-6">
          {children}
        </div>
      )}
    </div>
  );
};

export default function BookingDetailDrawer({ 
  booking, 
  event,
  open, 
  onClose, 
  authToken,
  onBookingUpdated 
}) {
  const [loading, setLoading] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // New dialog states for extracted components
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);

  // Fetch payment plan for this booking
  useEffect(() => {
    const fetchPaymentPlan = async () => {
      if (!booking?.booking_id || !authToken) return;
      
      try {
        const response = await fetch(
          `${API_URL}/api/weekends/admin/payment-plans?booking_id=${booking.booking_id}`,
          { headers: { 'Authorization': `Bearer ${authToken}` } }
        );
        if (response.ok) {
          const data = await response.json();
          // Handle both array and object response shapes
          const plans = Array.isArray(data) ? data : (data?.items || data?.plans || []);
          setPaymentPlan(plans.find(p => p.status === 'active' || p.status === 'paused') || null);
        }
      } catch (error) {
        console.error('Failed to fetch payment plan:', error);
      }
    };

    if (open) {
      fetchPaymentPlan();
    }
  }, [booking?.booking_id, authToken, open]);

  if (!booking) return null;

  const total = booking.total_override || booking.total_amount || booking.total_price || 0;
  const paid = booking.amount_paid || 0;
  const balance = Math.max(0, total - paid);
  const deposit = booking.deposit_override || booking.total_deposit || booking.deposit_due || 0;

  const guests = booking.guests || [];
  // Handle rooms as array (authoritative API format) or object (legacy)
  const roomsArray = Array.isArray(booking.rooms) ? booking.rooms : [];
  const addOns = booking.selected_add_ons || [];
  const payments = booking.payment_records || [];

  // Action handlers that invoke existing functionality
  const handleResendConfirmation = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/resend-confirmation`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      if (response.ok) {
        toast.success('Confirmation email sent');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send confirmation');
      }
    } catch (error) {
      toast.error('Failed to send confirmation');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReceipt = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/send-receipt`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      if (response.ok) {
        toast.success('Receipt sent');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to send receipt');
      }
    } catch (error) {
      toast.error('Failed to send receipt');
    } finally {
      setLoading(false);
    }
  };

  const handleFinanceVerifyToggle = async () => {
    const verify = !booking.finance_verified;
    if (verify && !window.confirm(`Mark ${booking.customer_name}'s booking financials as VERIFIED? Guests will then see balances for this booking again.`)) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/${verify ? 'finance-verify' : 'finance-unverify'}`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        toast.success(verify ? 'Booking marked finance-verified' : 'Booking returned to Balance Under Review');
        if (onBookingUpdated) onBookingUpdated();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update verification');
      }
    } catch (error) {
      toast.error('Failed to update verification');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/cancel`,
        {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ send_notification: true })
        }
      );
      if (response.ok) {
        toast.success('Booking cancelled');
        onBookingUpdated();
        setShowCancelDialog(false);
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to cancel booking');
      }
    } catch (error) {
      toast.error('Failed to cancel booking');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBooking = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      if (response.ok) {
        toast.success('Booking deleted');
        onBookingUpdated();
        onClose();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete booking');
      }
    } catch (error) {
      toast.error('Failed to delete booking');
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const handleChangeStatus = async (newStatus) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/weekends/admin/bookings/${booking.booking_id}/status`,
        {
          method: 'PATCH',
          headers: { 
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: newStatus })
        }
      );
      if (response.ok) {
        toast.success(`Status changed to ${statusLabels[newStatus]}`);
        onBookingUpdated();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to change status');
      }
    } catch (error) {
      toast.error('Failed to change status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/50 z-40 transition-opacity",
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className={cn(
        "fixed right-0 top-0 h-full w-full sm:w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300",
        open ? "translate-x-0" : "translate-x-full"
      )}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-4 z-10">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-violet-600">
                  {booking.booking_id}
                </span>
                <Badge className={cn("text-xs border", statusColors[booking.status])}>
                  {statusLabels[booking.status] || booking.status}
                </Badge>
                {!booking.finance_verified && (
                  <Badge data-testid="balance-under-review-badge" className="text-xs border bg-amber-100 text-amber-800 border-amber-200">
                    Balance Under Review
                  </Badge>
                )}
                <Button
                  data-testid="finance-verify-toggle-btn"
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  onClick={handleFinanceVerifyToggle}
                  className={cn("text-xs h-6 px-2", booking.finance_verified ? "text-amber-700 border-amber-300 hover:bg-amber-50" : "text-emerald-700 border-emerald-300 hover:bg-emerald-50")}
                >
                  {booking.finance_verified ? 'Unverify' : 'Mark Verified'}
                </Button>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Booked {formatDateTime(booking.created_at)}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Quick Financial Summary */}
          <div className="grid grid-cols-3 gap-4 mt-4 p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="text-xs text-slate-500">Total</p>
              <p className="font-semibold text-slate-900">{formatCurrency(total)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Paid</p>
              <p className="font-semibold text-emerald-600">{formatCurrency(paid)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Balance</p>
              <p className={cn(
                "font-semibold",
                balance > 0 ? "text-orange-600" : "text-slate-400"
              )}>
                {formatCurrency(balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto h-[calc(100%-180px)] px-4">
          {/* Customer Section */}
          <Section title="Customer" icon={User}>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <span className="font-medium text-slate-900">{booking.customer_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <a href={`mailto:${booking.customer_email}`} className="text-violet-600 hover:underline">
                  {booking.customer_email}
                </a>
              </div>
              {booking.customer_phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <a href={`tel:${booking.customer_phone}`} className="text-violet-600 hover:underline">
                    {booking.customer_phone}
                  </a>
                </div>
              )}
              {booking.special_requests && (
                <div className="mt-3 p-2 bg-yellow-50 rounded text-yellow-800 text-xs">
                  <strong>Notes:</strong> {booking.special_requests}
                </div>
              )}
            </div>
          </Section>

          {/* Guests Section */}
          <Section title={`Guests (${guests.length})`} icon={Users}>
            {guests.length > 0 ? (
              <div className="space-y-2 text-sm">
                {guests.map((guest, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1">
                    <span className="text-slate-700">
                      {guest.first_name} {guest.last_name}
                    </span>
                    {guest.email && (
                      <span className="text-xs text-slate-400">{guest.email}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No guest names recorded</p>
            )}
          </Section>

          {/* Room Section */}
          <Section title="Accommodation" icon={Hotel}>
            <div className="space-y-2 text-sm">
              {roomsArray.filter(r => r.quantity > 0).map((room, idx) => (
                <div key={idx} className="flex items-center justify-between py-1">
                  <span className="text-slate-700 capitalize">{room.room_type}</span>
                  <div className="text-right">
                    <span className="font-medium text-slate-900">x{room.quantity}</span>
                    {room.price_per_person && (
                      <span className="text-slate-500 text-xs ml-2">
                        ({formatCurrency(room.price_per_person)}/pp)
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {roomsArray.length === 0 && (
                <p className="text-slate-500">No rooms selected</p>
              )}
            </div>
          </Section>

          {/* Add-ons Section */}
          {addOns.length > 0 && (
            <Section title={`Add-ons (${addOns.length})`} icon={Bus}>
              <div className="space-y-2 text-sm">
                {addOns.map((addon, idx) => (
                  <div key={idx} className="flex items-center justify-between py-1">
                    <span className="text-slate-700">{addon.name}</span>
                    <span className="font-medium text-slate-900">
                      {formatCurrency(addon.total_price || addon.price)}
                    </span>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Payments Section */}
          <Section title={`Payment History (${payments.length})`} icon={CreditCard}>
            {payments.length > 0 ? (
              <div className="space-y-2">
                {payments.map((payment, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 text-sm border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-slate-700">
                        {formatCurrency(payment.amount)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {payment.payment_method || 'Card'} - {formatDate(payment.date || payment.created_at)}
                      </p>
                    </div>
                    {payment.notes && (
                      <span className="text-xs text-slate-400 max-w-32 truncate">
                        {payment.notes}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No payments recorded</p>
            )}
            
            {/* Payment Summary */}
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Deposit Required</span>
                <span className="text-slate-900">{formatCurrency(deposit)}</span>
              </div>
              {booking.discount?.amount > 0 && (
                <div className="flex justify-between" data-testid="booking-detail-discount">
                  <span className="text-emerald-600">
                    Discount{booking.discount.type === 'percentage' ? ` (${booking.discount.value}%)` : ''}
                    {booking.discount.reason ? ` — ${booking.discount.reason}` : ''}
                  </span>
                  <span className="text-emerald-600">−{formatCurrency(booking.discount.amount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Total Cost</span>
                <span className="text-slate-900">{formatCurrency(total)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-slate-700">Amount Paid</span>
                <span className="text-emerald-600">{formatCurrency(paid)}</span>
              </div>
              <div className="flex justify-between font-medium">
                <span className="text-slate-700">Balance Due</span>
                <span className={balance > 0 ? "text-orange-600" : "text-slate-400"}>
                  {formatCurrency(balance)}
                </span>
              </div>
            </div>
          </Section>

          {/* Payment Plan Section */}
          {paymentPlan && (
            <Section title="Payment Plan" icon={CalendarClock}>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Status</span>
                  <Badge variant="outline" className={cn(
                    paymentPlan.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                    paymentPlan.status === 'paused' ? 'bg-yellow-50 text-yellow-700' :
                    'bg-slate-50 text-slate-600'
                  )}>
                    {paymentPlan.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Monthly Amount</span>
                  <span className="font-medium text-slate-900">
                    {formatCurrency(paymentPlan.monthly_amount)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Remaining</span>
                  <span className="font-medium text-orange-600">
                    {formatCurrency(paymentPlan.remaining_balance)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Next Payment</span>
                  <span className="text-slate-900">
                    {formatDate(paymentPlan.next_payment_date)}
                  </span>
                </div>
                {paymentPlan.card_last4 && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Card</span>
                    <span className="text-slate-900">
                      {paymentPlan.card_brand} ****{paymentPlan.card_last4}
                    </span>
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-4 py-3">
          <div className="flex items-center gap-2">
            {/* Primary actions */}
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={handleResendConfirmation}
              disabled={loading}
            >
              <Send className="w-4 h-4 mr-1" />
              Confirmation
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={handleSendReceipt}
              disabled={loading || payments.length === 0}
            >
              <Receipt className="w-4 h-4 mr-1" />
              Receipt
            </Button>
            
            {/* More actions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setShowEditDialog(true)}
                  data-testid="drawer-edit-booking"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Booking
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setShowPaymentDialog(true)}
                  data-testid="drawer-record-payment"
                >
                  <PoundSterling className="w-4 h-4 mr-2" />
                  Record Payment
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => setShowInvoiceDialog(true)}
                  data-testid="drawer-view-invoice"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Invoice
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => handleChangeStatus('confirmed')}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark as Confirmed
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer"
                  onClick={() => handleChangeStatus('fully_paid')}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Mark as Fully Paid
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="cursor-pointer text-orange-600"
                  onClick={() => setShowCancelDialog(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel Booking
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="cursor-pointer text-red-600"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Booking
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Cancel Booking?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel booking <strong>{booking.booking_id}</strong> for {booking.customer_name}. 
              The customer will be notified by email and room availability will be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelBooking}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" />
              Delete Booking Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete booking <strong>{booking.booking_id}</strong>. 
              This action cannot be undone. Consider cancelling instead if you may need to restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Booking</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteBooking}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Booking Dialog - Extracted Component */}
      <EditWeekenderBookingDialog
        booking={booking}
        event={event}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        authToken={authToken}
        onSuccess={() => {
          setShowEditDialog(false);
          if (onBookingUpdated) onBookingUpdated();
        }}
      />

      {/* Record Payment Dialog - Extracted Component */}
      <RecordWeekenderPaymentDialog
        booking={booking}
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        authToken={authToken}
        onSuccess={() => {
          setShowPaymentDialog(false);
          if (onBookingUpdated) onBookingUpdated();
        }}
      />

      {/* Invoice Dialog - Extracted Component */}
      <WeekenderInvoiceDialog
        booking={booking}
        open={showInvoiceDialog}
        onClose={() => setShowInvoiceDialog(false)}
        authToken={authToken}
      />
    </>
  );
}
